import {CFMember, CFModel, CFValue} from 'module/CustomField/type/CFType'
import {CFAttrTO, CustomFieldTO, DqParamTO, SisType} from 'generated/JavaTO'
import {CF, CFMemberComparator} from 'module/CustomField/util/CustomField'
import {getDqParamsObject, parseDqParamFromSerializedString} from 'module/CustomField/util/DqParam'
import {Epic, StateObservable} from 'redux-observable'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {catchError, debounceTime, filter, map, mergeMap, pairwise, startWith, switchMap, take} from 'rxjs/operators'
import {getSisDateAsNumber, getSisDurationAsNumber, parseSisDate, parseSisDuration, parseSisTime} from 'util/SisDateTime'
import {from, merge, Observable, of} from 'rxjs'
import {DqElement, searchDq} from 'module/Dq/searchDq'
import {catchServerError} from 'module/ConfirmDialog/component/ConfirmDialogContainer'
import {PayloadAction} from 'util/redux'
import _ from 'lodash'
import {CFDefSpecificGeneratorParams} from 'module/CustomField/CustomFieldModule'
import {fetchGeoLocation} from 'module/module/geo/fetchGeoLocation'
import {toastWarn} from 'module/Toast/Toast'
import {parseServerError} from 'module/Error/util/parseServerError'
import {log} from 'common/log'
import {selBSCollectionByIdDict} from 'module/BSCollection/selector/BSCollectionSelector'
import {SisName} from 'util/SisName'
import {selStTagById} from 'module/StTag/selector/StTagSelector'
import {bsMergeResponseAction, bsRefreshCollectionAction} from 'module/BSCollection/action/BSCollectionAction'
import {stTagEnsureRefreshedAction, stTagResetAction} from 'module/StTag/action/StTagAction'
import {
	CFRoundResultMapperParams,
	getCFRoundModelParametersForStTag,
	getCFRoundResult,
	resolveCFRoundParameters,
	RoundDependencyPrefixes,
	RoundStTagPrefix,
} from 'module/CustomField/util/CFAttrRoundUtil'
import {AnyMatcher} from 'util/redux/matcher'
import {clipboardCopyAction} from 'module/Clipboard/action/ClipboardAction'
import {selClipboardMap} from 'module/Clipboard/selector/ClipboardSelector'
import {parsePasteClipboardCFAttrValue, PasteCFAttrKeyMap, pasteClipboardFromParsedParams, PastePhase} from 'module/Clipboard/util/pasteClipboardFromCFAttr'
import {ensureGroupedCFMissingNullValuesAreFilledAction} from 'module/CustomField/action/CustomFieldChangeHandlerAction'
import {getGroupedCFMissingNullValues} from 'module/CustomField/util/getGroupedCFMissingNullValues'
import {dqButtonClickedAction, dqButtonResponseAction} from 'module/DqButton/action/DqButtonsAction'
import {DqButtonLocation} from 'module/DqButton/type/DqButtonLocations'
import {isNullCFMember} from 'module/CustomField/util/isNullCFMember'

interface CFDataState {
	model: CFModel | null | undefined
	pos: number | undefined
	value: CFValue | CFMember
	params: ReturnType<typeof getDqParamsObject>
}

interface ComparableMap {
	positions: (number | undefined)[]
	data: CFDataState[]
}

interface CFDefChangeEvent {
	previous?: CFDataState
	current?: CFDataState
}

function genComparableMap(model: CFModel | undefined | null, cfDef: CustomFieldTO, dqParams: DqParamTO[]): ComparableMap {
	const positions = CF.getPositions([cfDef], model)
	return {
		positions,
		data: positions.map(pos => ({
			model,
			pos,
			value: CF.getValueFrom(cfDef, model, {pos}) as CFValue | CFMember,
			params: getDqParamsObject(dqParams, model, pos),
		})),
	}
}

function filterCFDefChange(state$: StateObservable<any>, cfDef: CustomFieldTO, selModel: CFDefSpecificGeneratorParams['selModel'], changingActionsMatcher: AnyMatcher, dqParams?: DqParamTO[]) {
	const dqp = dqParams || []
	return (obs$: Observable<PayloadAction<any>>) => obs$.pipe(
		filter(changingActionsMatcher),
		map(() => genComparableMap(selModel(state$.value), cfDef, dqp)),
		startWith(genComparableMap(selModel(state$.value), cfDef, dqp)),
		pairwise(),
		mergeMap(([previousMaps, currentMaps]): CFDefChangeEvent[][] => {
				const ret = [
					...currentMaps.data.flatMap((current) => {
						const previous = previousMaps.data.find(prev => current.pos === prev.pos)
						if (!previous || !_.isEqual(previous.value, current.value) || !_.isEqual(previous.params, current.params)) {
							return [{previous, current}]
						} else {
							// did not change
							return []
						}
					}),
					...previousMaps.data.flatMap((previous) => {
						if (!currentMaps.positions.includes(previous.pos)) return [{previous, current: undefined}]
						else return []
					}),
				]
				if (ret.length) return [ret]
				else return []
			},
		),
	)
}

function getClipboardValueToCopy(cfDef: CustomFieldTO, cfAttr: CFAttrTO, model: CFModel | null | undefined) {
	let value
	if (cfAttr.key === CFAttrKey.ClipboardCopySingleValue || cfAttr.key === CFAttrKey.ClipboardCopySingleValueOnOpen) {
		const wantedIndex = parseInt(cfAttr.type + '') || 0
		if (CF.isCFNamespace(cfDef)) {
			const dstField = CF.getCFNamespaceField(cfDef);
			const cf = model?.cf?.flatMap((member) =>
					member.dstField === dstField ? [{...member}] : [],
				).sort(CFMemberComparator)
				|| []
			value = cf[wantedIndex]?.data
		} else {
			const separator = CF.getCFAttr(cfDef, CFAttrKey.separator)?.value
			value = CF.getValueFrom(cfDef, model) as string
			if (separator && value) {
				value = (value + '').split(separator).filter(x => x)[wantedIndex]
			}
		}
	} else {
		value = CF.getValueFrom(cfDef, model || {})
	}
	if (value === undefined || typeof value === 'number' && isNaN(value)) value = null
	return value
}
/**
 * do a switchMap on comineEpics(...result)(action$, state$, dep)
 * only emit action, when there is really a change in the model!
 */
export function genCFDefChangeEventHandlerEpics({
	cfDefs,
	selModel,
	setAttrAction,
	setCFAction,
	changingActionsMatcher,
	closingActionsMatcher,
}: CFDefSpecificGeneratorParams): Epic[] {
	cfDefs = cfDefs.filter(cfDef => !CF.isLabelField(cfDef))
	const cfDefEpics = cfDefs.flatMap((cfDef) => {
			return cfDef.cfAttrs.flatMap((cfAttr): Epic[] => {
				if (cfAttr.key === CFAttrKey.preserveDateRangeForAttribute) {
					const dstField = cfAttr.value
					return [(action$, state$) => action$.pipe(
						filterCFDefChange(state$, cfDef, selModel, changingActionsMatcher),
						mergeMap(changes => changes.flatMap(({previous, current}) => {
							if (previous && current) {
								// only keep distance if there was a previous value
								const diff = parseSisDate(current.value as number).diff(parseSisDate(previous.value as number))
								if (diff) {
									const otherDate = +(current.model as any || {})[dstField]
									const movedDate = getSisDateAsNumber(parseSisDate(otherDate).add(diff, 'ms'))
									if (otherDate !== movedDate) {
										return [setAttrAction({[dstField]: movedDate})]
									}
								}
							}
							return []
						})),
					)]
				} else if (cfAttr.key === CFAttrKey.preserveDateRangeForCFDef) {
					const dstCFDefId = +cfAttr.value
					const dstCFDef = cfDefs.find(x => x.id === dstCFDefId)
					if (dstCFDef) {
						return [(action$, state$) => action$.pipe(
							filterCFDefChange(state$, cfDef, selModel, changingActionsMatcher),
							mergeMap(changes => changes.flatMap(({previous, current}) => {
								if (previous && current) {
									const diff = parseSisDate(current.value as number).diff(parseSisDate(previous.value as number))
									if (diff) {
										const otherDate = +(CF.getValueFrom(dstCFDef, current.model as CFModel) || NaN)
										const movedDate = getSisDateAsNumber(parseSisDate(otherDate).add(diff, 'ms'))
										if (otherDate !== movedDate) {
											return [setCFAction({cfDef, value: movedDate})]
										}
									}
								}
								return []
							})),
						)]
					}
				} else if (cfAttr.key === CFAttrKey.preserveEndTimeToStartTimeDstField) {
					const dstField = cfAttr.value
					const cfDefStartTime = cfDefs.find(c => c.dstField === dstField)
					if (!cfDefStartTime) {
						log.error("preserveEndTimeToStartTimeDstField: cfDef with dstField " + dstField + " not found", cfDefs)
						return []
					}
					return [(action$, state$) => action$.pipe(
						filterCFDefChange(state$, cfDefStartTime, selModel, changingActionsMatcher),
						mergeMap(changes => changes.flatMap(({previous, current}) => {
								if (!previous || !current) return []
								const previousDur = CF.getValueFrom(cfDef, previous.model, {pos: previous.pos})
								const currentDur = CF.getValueFrom(cfDef, current.model, {pos: current.pos})
								if (previousDur !== currentDur) {
									// duration changed also -> ignore adapting
									return []
								}
								const dur = CF.getValueFrom(cfDef, current.model, {pos: current.pos}) as number
								if (!dur) {
									// no duration, so start and end are the same -> do not keep end time
									return []
								}
								const prevStart = +parseSisTime(previous.value as number, {ignoreSeconds: true})
								const currStart = +parseSisTime(current.value as number, {ignoreSeconds: true})
								const timeFixedTimespan = getSisDurationAsNumber(parseSisDuration(dur).add(prevStart - currStart, 'ms'))
								if (timeFixedTimespan !== current.value) {
									// fix change of value removes selection -> reselect content
									setTimeout(() => (window.document.activeElement as HTMLInputElement)?.select?.(), 200)
									return [setCFAction({cfDef, value: timeFixedTimespan, options: {pos: current.pos}})]
								}
								return []
							}),
						))]
				} else if (cfAttr.key === CFAttrKey.remoteSetterDq) {
					const dqkey = cfAttr.value
					const discriminator = cfAttr.type
					const dqParams: DqParamTO[] = []
					const dstAttrs: string[] = []
					const dstCFDefs: CustomFieldTO[] = []
					cfDef.cfAttrs.forEach(x => {
						if (x.type !== discriminator) return
						else if (x.key === CFAttrKey.remoteSetterDqParam) dqParams.push(parseDqParamFromSerializedString(x.value))
						else if (x.key === CFAttrKey.remoteSetterDstAttr) {
							if (x.value.startsWith('cf.')) {
								const cfDef = cfDefs.find(c => c.dstField === x.value)
								if (cfDef) dstCFDefs.push(cfDef)
							} else {
								dstAttrs.push(x.value)
							}
						} else if (x.key === CFAttrKey.remoteSetterDstCFDefId) {
							const cfDefId = parseInt(x.value)
							const cfDef = cfDefs.find(c => c.id === cfDefId)
							if (cfDef) dstCFDefs.push(cfDef)
						}
					})
					if (!dstAttrs.length && !dstCFDefs.length) return []
					return [(action$, state$) => action$.pipe(
						filterCFDefChange(state$, cfDef, selModel, changingActionsMatcher, dqParams),
						mergeMap(x => x), //flatten
						mergeMap(({previous, current}) => {
							if (!current) return []
							return from(searchDq({
								dqkey,
								params: current.params,
								filter: current.value ? current.value + '' : '',
							})).pipe(
								mergeMap((data: DqElement[]) => {
									const model = selModel(state$.value)
									if (!model || !data || !data.length) return []
									const pos = current?.pos || 0
									if (data.some(d => isNaN(parseInt(d.pos + '')))) {
										// pos attribute required when multiple rows are returned
										// using current pos when no pos is returned
										data = [{...data[0], pos}]
									}
									return [
										...dstCFDefs.flatMap(cfDef => {
											if (CF.isCFNamespace(cfDef)) {
												return data.flatMap((row, i) => {
													const {value} = row
													let pos = parseInt(row.pos + '')
													if (isNaN(pos)) pos = data.length
													if (!pos || isNaN(parseInt(pos + ''))) pos = i
													return CF.getValueFrom(cfDef, model, {pos}) == value ? [] : [setCFAction({cfDef: cfDef, value, options: {pos}})]
												})
											} else {
												const row = data[0]
												if (!row || CF.getValueFrom(cfDef, model) == row.value) return []
												return [setCFAction({cfDef: cfDef, value: row.value})]
											}
										}),
										...dstAttrs.flatMap(attr => {
											const row = data[0]
											if (!row || model[attr] == row.value) return []
											return [setAttrAction({[attr]: row.value})]
										}),
									]
								}),
								catchServerError(),
							)
						}),
					)]
				} else if (cfDef.type === SisType.GPS && cfAttr.key === CFAttrKey.alwaysSet && cfAttr.value === 'J') {
					// set geolocation if not set
					return [(action$, state$) => action$.pipe(
						take(1),
						switchMap(() => {
							let model = selModel(state$.value)
							if (!isNullCFMember(CF.getValueFrom(cfDef, model, {pos: 0}) as CFValue | CFMember)) return []
							return from(fetchGeoLocation()).pipe(
								mergeMap(({coords}: GeolocationPosition) => {
									if (!coords || !isNullCFMember(CF.getValueFrom(cfDef, model, {pos: 0}) as CFValue | CFMember)) return []
									let {latitude, longitude, accuracy} = coords
									return [setCFAction({
										cfDef,
										value: {lat: latitude, lon: longitude, acc: accuracy, pos: 0},
									})]
								}),
								catchError((error) => {
									toastWarn(parseServerError(error), "gpsWarn", {autoClose: 3000})
									return []
								}),
							)
						}),
					)]
				} else if (cfAttr.key === CFAttrKey.ClipboardCopyOnOpen || cfAttr.key === CFAttrKey.ClipboardCopySingleValueOnOpen) {
					const clipboardName = cfAttr.value?.trim();
					if (!clipboardName) return []
					return [(action$, state$) => of(1).pipe(
						mergeMap(() => {
							const model = selModel(state$.value)
							const value = getClipboardValueToCopy(cfDef, cfAttr, model)
							return [
								clipboardCopyAction({
									clipboardName,
									value,
								}),
							]
						}))]
				} else if (cfAttr.key === CFAttrKey.ClipboardCopy || cfAttr.key === CFAttrKey.ClipboardCopySingleValue) {
					const clipboardName = cfAttr.value?.trim();
					if (!clipboardName) return []
					return [(action$, state$) => action$.pipe(
						filterCFDefChange(state$, cfDef, selModel, changingActionsMatcher),
						mergeMap(() => {
							const model = selModel(state$.value)
							let value = getClipboardValueToCopy(cfDef, cfAttr, model)
							const currentValue = selClipboardMap(state$.value)[clipboardName]
							if (_.isEqual(value, currentValue)) return []
							return [
								clipboardCopyAction({
									clipboardName,
									value,
								}),
							]
						}))]
				} else if (cfAttr.key === CFAttrKey.ClipboardCopyOverride) {
					const clipboardName = cfAttr.value?.trim();
					if (clipboardName) {
						return [(action$, state$) => action$.pipe(
							filter(action => clipboardCopyAction.match(action) && action.payload.clipboardName === clipboardName),
							mergeMap(({payload}) => {
								const model = selModel(state$.value)
								const value = getClipboardValueToCopy(cfDef, cfAttr, model)
								if (
									value &&
									(!Array.isArray(value) || value.filter(x => !isNullCFMember(x)).length > 0) &&
									!_.isEqual(payload.value, value) &&
									!_.isEqual(selClipboardMap(state$.value)[clipboardName], value)
								) {
									return [clipboardCopyAction({clipboardName, value})]
								}
								return []
							}),
						)]
					}
				} else if (cfAttr.key === CFAttrKey.ClipboardClear) {
					const clipboardName = cfAttr.value?.trim();
					if (clipboardName) {
						return [(action$, state$) => action$.pipe(
							filter(closingActionsMatcher),
							mergeMap(() => {
								const currentValue = selClipboardMap(state$.value)[clipboardName]
								if (currentValue || currentValue === 0) return [clipboardCopyAction({clipboardName, value: null})]
								else return []
							}))]
					}
				} else if (cfAttr.key === CFAttrKey.ClipboardClearOnChange) {
					const clipboardName = cfAttr.value?.trim();
					if (clipboardName) {
						return [(action$, state$) => action$.pipe(
							filterCFDefChange(state$, cfDef, selModel, changingActionsMatcher),
							mergeMap(() => {
								const currentValue = selClipboardMap(state$.value)[clipboardName]
								if (currentValue || currentValue === 0) return [clipboardCopyAction({clipboardName, value: null})]
								else return []
							}))]
					}
				} else if (PasteCFAttrKeyMap[PastePhase.CHANGE].includes(cfAttr.key as CFAttrKey)) {
					const pasteParams = parsePasteClipboardCFAttrValue(cfAttr, cfDef)
					return [
						(action$, state$) => action$.pipe(
							filter((action) =>
								clipboardCopyAction.match(action) &&
								pasteParams.dependentClipNames.includes(action.payload.clipboardName),
							),
							debounceTime(20),
							mergeMap(() => {
									const clipboard = selClipboardMap(state$.value)
									const model = selModel(state$.value)
									const pasteActionParams = pasteClipboardFromParsedParams(pasteParams, cfDef, cfDefs, clipboard, model || {}, false)
									const pasteActions = pasteActionParams.map(({cfDef, value, options}) => setCFAction({cfDef, value, options}))
									log.trace('clipboard paste actions', pasteActionParams, pasteParams, cfDef, clipboard, model)
									if (pasteActions.length) return [...pasteActions, ensureGroupedCFMissingNullValuesAreFilledAction()]
									return []
								},
							),
						),
					]
				} else if (cfAttr.key === CFAttrKey.roundTime || cfAttr.key === CFAttrKey.roundDuration) {
					const exceptions = cfDef.cfAttrs.flatMap(x => x.key === CFAttrKey.roundException ? [x.value] : [])
					return [(action$, state$) => action$.pipe(
						filterCFDefChange(state$, cfDef, selModel, changingActionsMatcher),
						mergeMap(x => x), // flatten
						mergeMap(({current}) => {
							if (!current) return []
							const baseParams: Pick<
								CFRoundResultMapperParams,
								'model' | 'cfDef' | 'cfAttr' | 'exceptions' | 'allCFDefs'
							> = {cfDef, cfAttr, model: current.model, allCFDefs: cfDefs}
							const pos = current.pos
							const actionParams = {cfDef, options: {pos}}
							const dependenciesRequired = RoundDependencyPrefixes.filter(dep => [cfAttr.value, cfAttr.type, ...exceptions].some(x => x?.startsWith(dep)))
							if (!dependenciesRequired.length) {
								const value = getCFRoundResult({
									...baseParams,
									value: current.value as number,
									pos,
									...resolveCFRoundParameters(cfAttr, exceptions),
								})
								if (value !== current.value) return [setCFAction({...actionParams, value})]
								else return []
							}
							//dependency resolution needed
							return merge(...dependenciesRequired.flatMap((dep): Parameters<typeof merge> => {
								if (dep === RoundStTagPrefix) {
									if (!current.model) return []
									const model = current.model
									const {persNr, date, dateTime} = getCFRoundModelParametersForStTag(model)
									if (!persNr || !date) {
										log.debug('missing data for change handler:', cfAttr, model, persNr, date)
										return []
									}
									const stTagZu = selBSCollectionByIdDict[SisName.StTagZu](state$.value)[persNr + '_' + date]
									const stTag = selStTagById(state$.value)[stTagZu?.stTagKey]
									if (stTag) {
										const value = getCFRoundResult({
											...baseParams,
											value: current.value as number,
											pos,
											...resolveCFRoundParameters(cfAttr, exceptions, stTag),
										})
										if (current.value !== value) return [[setCFAction({...actionParams, value})]]
										else return []
									} else {
										const depActions = [
											//todo block saving! or add validation
										]
										if (!stTagZu) {
											depActions.push(bsRefreshCollectionAction({
												sisName: SisName.StTagZu,
												overrideOwners: [persNr],
												from: +dateTime.startOf('day'),
												to: +dateTime.endOf('day'),
												force: true,
											}))
										}
										if (!stTag) {
											depActions.push(stTagEnsureRefreshedAction())
										}
										return [merge(
											from(depActions),
											action$.pipe(
												filter(action =>
													stTagResetAction.match(action) ||
													bsMergeResponseAction.match(action) &&
													action.payload.sisName == SisName.StTagZu &&
													action.payload.owners.includes(persNr) &&
													action.payload.fetchDates.includes(date),
												),
												mergeMap(() => {
													const stTagZu = selBSCollectionByIdDict[SisName.StTagZu](state$.value)[persNr + '_' + date]
													const stTag = selStTagById(state$.value)[stTagZu?.stTagKey]
													if (dependenciesRequired.includes(RoundStTagPrefix) && !stTag) return []
													return [{stTag}]
												}),
												// all dependecies resolved
												mergeMap(({stTag}) => {
													log.debug(
														'cfRoundResultMapper called', current.value, pos, cfAttr,
														'resolved dependencies', stTag,
													)
													const value = getCFRoundResult({
														...baseParams,
														value: current.value as number,
														pos,
														...resolveCFRoundParameters(cfAttr, exceptions, stTag),
													})
													if (value !== current.value) return [setCFAction({...actionParams, value})]
													else return []
												}),
												take(1),
											),
										)]
									}
								}
								return []
							}))
						}),
					)]
				}
				return []
			});
		},
	)

	const ensureNullValuesInCFGroupEpic: Epic = (action$, state$) => action$.pipe(
		filter(ensureGroupedCFMissingNullValuesAreFilledAction.match),
		debounceTime(50), // wait for all setters to settle
		mergeMap(() => {
			const model = selModel(state$.value)
			if (!model) return []
			return getGroupedCFMissingNullValues(cfDefs, model)
				.map(({cfDef, pos}) =>
					setCFAction({cfDef, value: null, options: {pos}}),
				)
		}),
	)

	const dqButtonSinkEpic: Epic = (action$, state$) => action$.pipe(
		filter(dqButtonClickedAction.match),
		switchMap((requestAction) => {
				if (!cfDefs.length || requestAction.payload.dqButton.location !== DqButtonLocation.CFDefGroup) return []
				return action$.pipe(
					filter(dqButtonResponseAction.match),
					take(1),
					mergeMap((resultAction) => {
						const ret = resultAction.payload.data.elements.flatMap(x => {
							const {label, value, pos} = x
							const cfDef = cfDefs.find(cfDef => cfDef.id === Number(label) || cfDef.dstField === label)
							if (!cfDef) return []
							return [setCFAction({cfDef, value, options: {pos: Number(pos)}})]
						})
						if (ret.length) return [...ret, ensureGroupedCFMissingNullValuesAreFilledAction()]
						else return []
					}),
				)
			},
		),
	)

	const globalEpics: Epic[] = [
		dqButtonSinkEpic,
		ensureNullValuesInCFGroupEpic,
	]

	return [
		...cfDefEpics,
		...globalEpics,
	]
}